import { toUnityFixed } from '../../../../helpers/unityHelper';
import { callUnityEventModifyProperty, changeLayerSequence, enableGameObject, disableGameObject, snapButtonsToParentObject } from '../ProductBuilderComponents/commonHelper';
import { waitForEventOnce, UnityEvent } from '../../../../helpers/unityPubSub';
import { selectAddedViewData } from '../../../../redux/slicers/productBuilder/productBuilder.slicer';
import { getAppState } from '../../../../redux/store';
import { undoRedo } from './UndoRedo';

export class UndoRedoLayer {
  constructor({ undoRedoInstance }) {
    this.undoRedo = undoRedoInstance;
    this.pending = new Map();
    this.createAction = this.undoRedo.createAction;
    this._init();
  }

  _init() {
    // Single layer height change
    this._pushHeightChange = this.createAction(
      ({ prevValue, nextValue, objectName, updateCallback }) => {
        if (prevValue === nextValue) return false;

        return {
          __ACTION__: 'LAYER_HEIGHT_CHANGE',
          undo: async () => {
            window.$stopOpenModal = true
            await this._applyHeightChange(objectName, prevValue);
            updateCallback?.(objectName, prevValue);
          },
          redo: async () => {
            window.$stopOpenModal = true
            await this._applyHeightChange(objectName, nextValue);
            updateCallback?.(objectName, nextValue);
          },
        };
      },
      { pause: true, name: 'LAYER_HEIGHT_CHANGE' }
    );

    // Multiple layer height changes (batched)
    this._pushMultiHeightChange = this.createAction(
      ({ changes, updateCallback }) => {
        const hasChange = changes.some((c) => c.prevValue !== c.nextValue);
        if (!hasChange) return false;

        return {
          __ACTION__: 'LAYER_MULTI_HEIGHT_CHANGE',
          undo: async () => {
            for (const c of changes.slice().reverse()) {
              await this._applyHeightChange(c.objectName, c.prevValue);
              updateCallback?.(c.objectName, c.prevValue);
            }
          },
          redo: async () => {
            for (const c of changes) {
              await this._applyHeightChange(c.objectName, c.nextValue);
              updateCallback?.(c.objectName, c.nextValue);
            }
          },
        };
      },
      { pause: true, name: 'LAYER_MULTI_HEIGHT_CHANGE' }
    );

    // Layer sequence change (drag and drop)
    this._pushSequenceChange = this.createAction(
      ({ prevSequence, nextSequence, updateCallback }) => {
        // Check if sequence actually changed
        const hasChange = prevSequence.some((item, index) => 
          item.objectName !== nextSequence[index]?.objectName
        );
        
        if (!hasChange) return false;

        return {
          __ACTION__: 'LAYER_SEQUENCE_CHANGE',
          undo: async () => {
            await this._applySequenceChange(prevSequence);
            updateCallback?.(prevSequence);
          },
          redo: async () => {
            await this._applySequenceChange(nextSequence);
            updateCallback?.(nextSequence);
          },
        };
      },
      { pause: true, name: 'LAYER_SEQUENCE_CHANGE' }
    );

    // Add/Remove layer action
    this._pushLayerAddRemove = this.createAction(({ objectName, componentType, payload , refreshCallback }) => {
      if (!objectName) return false;
      return {
        __ACTION__: 'LAYER_ADD_REMOVE',
        undo: async () => {
          window.$stopOpenModal = true
          disableGameObject({
            propInfoCommandDatas: [{ objectName }],
            ...(payload || {})
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate); 
            
          // Refresh callback will be called after wait(50) in UndoRedo.js
          refreshCallback?.();
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);

        },
        redo: async () => {
          window.$stopOpenModal = true
          enableGameObject({
            propInfoCommandDatas: [{ objectName }],
            ...(payload || {})
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
            
          // Refresh callback will be called after wait(50) in UndoRedo.js
          refreshCallback?.();
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        
        },
      };
    });

    // Replace layer action (disable old, enable new)
    this._pushLayerReplace = this.createAction(({ 
      oldObjectName, 
      newObjectName, 
      componentType, 
      payload,
      refreshCallback 
    }) => {
      if (!oldObjectName || !newObjectName) return false;
      return {
        __ACTION__: 'LAYER_REPLACE',
        undo: async () => {
          
          // First: Disable the new layer
          disableGameObject({
            propInfoCommandDatas: [{ objectName: newObjectName }],
            ...(payload || {})
          });
          
          // Small delay to ensure Unity processes
          // await new Promise(resolve => setTimeout(resolve, 50));
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
               
          
          // Second: Enable the old layer
          enableGameObject({
            propInfoCommandDatas: [{ objectName: oldObjectName }],
            ...(payload || {})
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
          
          // Refresh after both operations
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);

        },
        redo: async () => {
          
          // First: Disable the old layer
          disableGameObject({
            propInfoCommandDatas: [{ objectName: oldObjectName }],
            ...(payload || {})
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
          
          // Small delay to ensure Unity processes
          // await new Promise(resolve => setTimeout(resolve, 50));
          
          // Second: Enable the new layer
          enableGameObject({
            propInfoCommandDatas: [{ objectName: newObjectName }],
            ...(payload || {})
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
          
          // Refresh after both operations
          refreshCallback?.();
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);

        },
      };
    });

    // Delete layer action
    this._pushLayerDelete = this.createAction(({ 
      deletedObjectName, 
      componentType, 
      payload,
      refreshCallback 
    }) => {
      if (!deletedObjectName) return false;
      return {
        __ACTION__: 'LAYER_DELETE',
        undo: async () => {
          window.$stopOpenModal = true
          // Enable the deleted layer
          enableGameObject({
            propInfoCommandDatas: [{ objectName: deletedObjectName }],
            ...(payload || {})
          });
          
          // Small delay to ensure Unity processes
          // await new Promise(resolve => setTimeout(resolve, 10));
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
          
          // Refresh after operation
          refreshCallback?.();
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);

        },
        redo: async () => {
          window.$stopOpenModal = true
          // Disable the layer again
          disableGameObject({
            propInfoCommandDatas: [{ objectName: deletedObjectName }],
            ...(payload || {})
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);         
            
          
          // Small delay to ensure Unity processes
          // await new Promise(resolve => setTimeout(resolve, 10));
          
          // Refresh after operation
          refreshCallback?.();
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);  
        },
      };
    });
  }

  async _applyHeightChange(objectName, heightValue) {
    callUnityEventModifyProperty(
      heightValue,
      'internalLayerHeight',
      objectName,
      '',
      'InternalLayer',
      true
    );
    await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
    snapButtonsToParentObject()
  }

  async _applySequenceChange(sequence) {
    // Get the layer view data from Redux
    const layerView = selectAddedViewData(getAppState());
    const valueForView =
      layerView?.view === 'exploded'
        ? (layerView?.[`gap${layerView?.view}`] || 0) * 0.35
        : layerView?.[`gap${layerView?.view}`] || 0;
    const internalLayerView = layerView?.view?.charAt(0)?.toUpperCase() + layerView?.view?.slice(1);

    // Apply the sequence change to Unity for each item sequentially
    for (let i = 0; i < sequence.length; i++) {
      const payload = {
        internalLayerView,
        newIndexForInsert: i,
        objectName: sequence[i].objectName,
        valueForView,
      };
      changeLayerSequence(payload);
      
      // Small delay to ensure Unity processes sequentially
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  // Start tracking height change
  startHeightChange({ objectName, initialValue }) {
    const key = `layer__${objectName}`;
    const normalizedValue = toUnityFixed(initialValue, 3);

    this.pending.set(key, {
      objectName,
      initialValue: normalizedValue,
      lastValue: normalizedValue,
      startTime: Date.now(),
    });
  }

  // Update pending height change (intermediate values)
  updatePendingChange({ objectName, currentValue }) {
    const key = `layer__${objectName}`;
    const item = this.pending.get(key);
    if (!item) return;

    const normalizedValue = toUnityFixed(currentValue, 3);
    item.lastValue = normalizedValue;
    this.pending.set(key, item);
  }

  // End tracking and create undo/redo action
  endHeightChange({ objectName, finalValue, updateCallback }) {
    const key = `layer__${objectName}`;
    const item = this.pending.get(key);
    if (!item) return;

    const prevValue = item.initialValue;
    const nextValue = toUnityFixed(finalValue, 3);

    this._pushHeightChange({
      prevValue,
      nextValue,
      objectName,
      updateCallback,
    });

    this.pending.delete(key);
  }

  // Batch multiple height changes
  endMultiHeightChanges({ changes, updateCallback }) {
    if (!changes || changes.length === 0) return;

    const processedChanges = changes
      .map((change) => {
        const key = `layer__${change.objectName}`;
        const item = this.pending.get(key);
        if (!item) return null;

        return {
          objectName: change.objectName,
          prevValue: item.initialValue,
          nextValue: toUnityFixed(change.finalValue, 3),
        };
      })
      .filter(Boolean);

    if (processedChanges.length === 0) return;

    if (processedChanges.length === 1) {
      // Single change
      this._pushHeightChange({
        ...processedChanges[0],
        updateCallback,
      });
    } else {
      // Multiple changes
      this._pushMultiHeightChange({
        changes: processedChanges,
        updateCallback,
      });
    }

    // Clear all pending
    processedChanges.forEach(({ objectName }) => {
      const key = `layer__${objectName}`;
      this.pending.delete(key);
    });
  }

  // Cancel pending change
  cancelHeightChange({ objectName }) {
    const key = `layer__${objectName}`;
    this.pending.delete(key);
  }

  // Clear all pending changes
  clearAllPendingChanges() {
    this.pending.clear();
  }

  // Add sequence change for undo/redo
  addSequenceChange({ prevSequence, nextSequence, updateCallback }) {
    // Create deep copies to prevent reference issues
    const prevSeq = prevSequence.map(item => ({
      objectName: item.objectName,
      id: item.id,
      name: item.name,
      layerHeight: item.layerHeight,
    }));
    
    const nextSeq = nextSequence.map(item => ({
      objectName: item.objectName,
      id: item.id,
      name: item.name,
      layerHeight: item.layerHeight,
    }));

    this._pushSequenceChange({
      prevSequence: prevSeq,
      nextSequence: nextSeq,
      updateCallback,
    });
  }

  addLayerAddAction({ objectName, componentType, payload, refreshCallback }) {
    this._pushLayerAddRemove({
      objectName,
      componentType,
      payload,
      refreshCallback,
    });
    undoRedo.pause();
  }

  addLayerReplaceAction({ oldObjectName, newObjectName, componentType, payload, refreshCallback }) {
    this._pushLayerReplace({
      oldObjectName,
      newObjectName,
      componentType,
      payload,
      refreshCallback,
    });
     undoRedo.pause();
  }

  addLayerDeleteAction({ deletedObjectName, componentType, payload, refreshCallback }) {
    this._pushLayerDelete({
      deletedObjectName,
      componentType,
      payload,
      refreshCallback,
    });
  }
}
